import { useState, useEffect } from 'react'
import Header from '@components/Header/Header'
import AccountNumberInput from '@components/AccountNumberInput/AccountNumberInput'
import NumericKeyboard from '@components/NumericKeyboard/NumericKeyboard'
import PrimaryButton from '@components/PrimaryButton/PrimaryButton'
import AmountOption from '@components/AmountOption/AmountOption'
import LoadingModal from '@components/LoadingModal/LoadingModal'
import Snackbar from '@components/Snackbar/Snackbar'
import { useNavigate, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { usePlnInquiry, PlnCustomerDetails } from '@hooks/usePlnInquiry'
import { usePpobProducts, PpobProduct } from '@hooks/usePpobProducts'
import { PpobProductCategory } from '@hooks/usePpobServices'
import { setHeaderColor } from '@lib/telegram'
import { WHITE } from '@styles/colors'
import {
  Container,
  ContentContainer,
  InputSection,
  InputWrapper,
  KeyboardSection,
  AmountSection,
  AmountLabel,
  AmountGrid,
  ButtonWrapper
} from './PLN.styles'

const PLN = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { isLoading, inquireCustomer } = usePlnInquiry()
  const { products, isLoading: isLoadingProducts, fetchProducts } = usePpobProducts()

  const state = location.state as { category?: PpobProductCategory } | undefined
  const category = state?.category
  const [step, setStep] = useState<'input' | 'amount'>('input')
  const [accountNumber, setAccountNumber] = useState('')
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<PpobProduct | null>(null)
  const [customerDetails, setCustomerDetails] = useState<PlnCustomerDetails | null>(null)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [showSnackbar, setShowSnackbar] = useState(false)

  useEffect(() => {
    setHeaderColor(WHITE)
  }, [])

  const handleKeyboardChange = (value: string | 'delete') => {
    if (value === 'delete') {
      setAccountNumber(prev => prev.slice(0, -1))
    } else {
      setAccountNumber(prev => prev + value)
    }
  }

  const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccountNumber(e.target.value)
  }

  const handleContinue = async () => {
    if (step === 'input') {
      // Inquire PLN customer first
      const result = await inquireCustomer(accountNumber)

      if (!result) {
        setSnackbarMessage(t('pln.errors.unableToValidate'))
        setShowSnackbar(true)
        return
      }

      if (!result.is_valid) {
        setSnackbarMessage(t('pln.errors.invalidAccountNumber'))
        setShowSnackbar(true)
        return
      }

      // Store customer details
      setCustomerDetails(result.customer)

      // Fetch PLN products
      await fetchProducts({
        category: category,
        brand: 'PLN',
        size: 100,
        sortBy: 'sellingPrice'
      })

      // Proceed to amount selection
      setStep('amount')
    } else {
      // Navigate to payment confirm page
      navigate('/payment-confirm', {
        state: {
          accountNumber,
          customerName: customerDetails?.name,
          amount: selectedAmount,
          service: 'pln',
          customerDetails: customerDetails,
          productAmount: selectedProduct?.productAmount,
          product: selectedProduct,
          paymentType: 'prepaid',
          adminFee: selectedProduct?.adminFee,
          platformFee: selectedProduct?.platformFee
        }
      })
    }
  }

  const handleAmountSelect = (index: number) => {
    setSelectedAmount(products[index].sellingPriceNet)
    setSelectedProduct(products[index])
  }

  const handleSnackbarClose = () => {
    setShowSnackbar(false)
  }

  const handleBack = () => {
    if (step === 'amount') {
      setStep('input')
      setSelectedAmount(null)
      setSelectedProduct(null)
    } else {
      navigate(-1)
    }
  }

  return (
    <Container>
      <LoadingModal visible={isLoading || isLoadingProducts} />
      <Snackbar
        message={snackbarMessage}
        isVisible={showSnackbar}
        onClose={handleSnackbarClose}
        textAlign="center"
        width="100%"
      />
      <Header
        title={t('pln.header')}
        showBackButton={true}
        onBackClick={handleBack}
      />
      <ContentContainer>
        <InputSection>
          <InputWrapper>
            <AccountNumberInput
              title={t('pln.accountNumberLabel')}
              value={accountNumber}
              onChange={handleAccountNumberChange}
              placeholder={t('pln.accountNumberPlaceholder')}
              inputMode="none"
            />
          </InputWrapper>
        </InputSection>

        {step === 'input' ? (
          <KeyboardSection>
            <NumericKeyboard onChange={handleKeyboardChange} />
            <PrimaryButton
              onClick={handleContinue}
              disabled={!accountNumber}
            >
              {t('common.continue')}
            </PrimaryButton>
          </KeyboardSection>
        ) : (
          <>
            <AmountSection>
              <AmountLabel>{t('pln.chooseAmount')}</AmountLabel>
              {products.length > 0 ? (
                <AmountGrid>
                  {products.map((product, index) => (
                    <div key={product.id} onClick={() => handleAmountSelect(index)}>
                      <AmountOption
                        idr={product.productAmount}
                        jidr={product.sellingPrice}
                        selected={selectedAmount === product.sellingPrice}
                      />
                    </div>
                  ))}
                </AmountGrid>
              ) : (
                <AmountLabel>{t('pln.noProductsAvailable')}</AmountLabel>
              )}
            </AmountSection>
            <ButtonWrapper>
              <PrimaryButton
                onClick={handleContinue}
                disabled={!selectedAmount}
              >
                {t('common.continue')}
              </PrimaryButton>
            </ButtonWrapper>
          </>
        )}
      </ContentContainer>
    </Container>
  )
}

export default PLN
